<template>
  <div v-if="services" class="w-full px-6 py-4">
    <div class="flex w-full items-center justify-between pb-4 border-b mb-8">
      <div>
        <label class="block text-sm text-gray-700 font-medium dark:text-gray-200">
          <span>{{ $t('general.services') }}</span>
        </label>
        <p class="block text-gray-600 text-sm dark:text-gray-300">
          {{ $t('components.pass.edit.services.info') }}
        </p>
      </div>
      <div class="relative">
        <text-field v-model="query" :placeholder="$t('service.filter')" />
        <div class="absolute right-0 transform mr-2 top-1/2 -translate-y-1/2">
          <o-button
            v-if="query.length"
            size="xs"
            variant="danger"
            transparent
            flat
            icon="x"
            @click="query = ''"
          />
        </div>
      </div>
    </div>
    <!--<div class="border-b p-2 mb-8">
      <div
        class="w-full flex items-center justify-between mb-2 pb-2 px-1 border-b last:border-none last:mb-0 last:pb-0"
      >
        <p class="text-sm" :class="_all ? 'text-gray-700' : 'text-gray-300'">
          Enable/disable all services
        </p>
        <toggle-field v-model="_all" />
      </div>
    </div>-->

    <template v-for="(category, index) in filtered" :key="`category-${index}`">
      <div v-if="category.name || category.services">
        <div v-if="category" class="px-1 mb-3">
          <h5 class="text-gray-700 text-sm font-medium uppercase tracking-wide dark:text-gray-200">
            {{ category.name || $t('general.other') }}
          </h5>
          <p v-if="category.information" class="text-sm text-gray-500 dark:text-gray-400">
            {{ category.information }}
          </p>
        </div>
        <div class="bg-gray-50 rounded-md p-3 mb-8 dark:bg-gray-900">
          <div class="grid grid-cols-12 border-b text-xs text-gray-500 dark:text-gray-400 uppercase">
            <p class="col-span-1 p-1 pb-2">
              {{ $t('general.enabled') }}
            </p>
            <p class="col-span-7 p-1 pb-2">
              {{ $t('general.service') }}
            </p>
            <p class="col-span-2 p-1 pb-2">
              {{ $t('components.pass.edit.services.servicePrice') }}
            </p>
            <p class="col-span-2 p-1 pb-2">
              {{ $t('components.pass.edit.services.usesDeducted') }}
            </p>
          </div>

          <div
            v-for="service in category.services"
            :key="service.service.idService"
            class="border-b last:border-none grid grid-cols-12 items-center"
          >
            <div class="col-span-1 p-1 py-2">
              <toggle-field v-model="service.enabled" />
            </div>

            <div class="col-span-7 text-sm line-clamp-1 p-1 flex items-center gap-2" :class="service.enabled ? 'text-gray-700 dark:text-gray-200' : 'text-gray-300 dark:text-gray-600'">
              {{ service.service.title }}

              <o-history-link
                v-if="service.passService.idPassService && (isAdmin || isDev)"
                :item="service.passService"
                icon="history"
                size="sm"
                slim
                model="PassService"
              />
            </div>

            <div class="col-span-2 p-1">
              <currency-field v-model="service.price" :disabled="!service.enabled" />
            </div>

            <div class="col-span-2 p-1">
              <number-field v-model="service.usesPerService" slim :disabled="!service.enabled" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import Fuse from 'fuse.js'

import { useServiceStore } from '~/stores/useServiceStore'

import {
  OTable,
} from '@/components/elements'

import {
  TextField,
  CurrencyField,
  SelectField,
  NumberField,
  TextAreaField,
  ToggleField,
  SegmentField,
} from '@/components/fields'

interface Props {
  pass: Record<string, any>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:pass', value: Record<string, any>): void
}>()

const serviceStore = useServiceStore()

const services = ref<any[]>([])
const query = ref('')

const categories = computed(() => serviceStore.categories)

const _pass = computed({
  get () {
    return props.pass
  },
  set (value) {
    emit('update:pass', value)
  },
})

const filtered = computed(() => {
  const q = query.value
  const svcs = services.value

  if (q?.length) {
    const fuse = new Fuse(svcs, {
      keys: [
        'service.title',
        'service.information',
        'service.price',
      ],
    })
    const results = fuse.search(q)
    const data = [
      {
        name: 'Filtered',
        services: results.map(({ item }) => item),
      },
    ]
    return data
  }

  const cats: Record<string, any> = {
    0: { services: [] },
  }

  for (const category of categories.value) {
    if (category) {
      cats[category.idServiceCategory] = {
        ...category,
        services: [],
      }
    }
  }

  for (const service of svcs) {
    const id = service.service.idServiceCategory || 0

    if (cats[id] && cats[id].services) {
      cats[id].services?.push(service)
    }
  }

  return cats
})

const isAdmin = computed(() => {
  return (getCurrentInstance()?.proxy as any)?.$auth?.info?.adminUser
})

const isDev = computed(() => {
  return (getCurrentInstance()?.proxy as any)?.$config?.public?.instance === 'dev'
})

function setServices () {
  const passServices = Object.fromEntries(props.pass.services.map((obj: any) => [Number(obj.idService), obj]))
  const svcs = props.pass.options.services

  services.value = svcs
    .map((service: any) => {
      const passService = passServices[service.idService] || service

      return {
        service,
        passService,
        enabled: !!passServices[service.idService],
        idService: Number(passService.idService),
        price: passService.price !== undefined && passService.price !== null ? passService.price : service.price,
        usesPerService: passService.usesPerService !== undefined && passService.usesPerService !== null ? passService.usesPerService : 1,
      }
    })
}

watch(_pass, () => {
  setServices()
}, { immediate: true })

watch(services, (svcs) => {
  const passServices: any[] = []
  for (const service of svcs) {
    if (service.enabled) {
      const { idService, price, usesPerService } = service
      passServices.push({ idService, price, usesPerService })
    }
  }

  _pass.value.services = passServices
}, { deep: true })
</script>
